import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Badge, useToast, Dialog, DialogContent, DialogHeader, DialogTitle } from '@hiring-intelligence/ui';
import { cvGenerationApi, sourcingApi } from '@/services/api';
import { CVPreviewModal } from './CVPreviewModal';
import { FileText, RefreshCw, Eye, CheckCircle, Download, Upload, AlertCircle, History, RotateCcw } from 'lucide-react';

interface CVGenerationPanelProps {
	sourcedCandidateId: string;
	hiringRequestId: string;
	hasCvFile?: boolean;
	hasSignalHireData?: boolean;
	cvOriginalName?: string;
	onCvUploaded?: () => void;
}

const CV_TYPES = [
	{ value: 'standard' as const, label: 'Standard CV', description: 'Full professional CV with enriched data', required: true },
	{ value: 'blind' as const, label: 'Blind CV', description: 'Anonymized CV for unbiased screening', required: false },
	{ value: 'consultant_notes' as const, label: 'Consultant Notes', description: 'TA assessment and insights', required: true },
];

export function CVGenerationPanel({ sourcedCandidateId, hiringRequestId, hasCvFile, hasSignalHireData, cvOriginalName, onCvUploaded }: CVGenerationPanelProps) {
	const { toast } = useToast();
	const queryClient = useQueryClient();
	const [previewCV, setPreviewCV] = useState<{ content: string; type: string; id?: string } | null>(null);
	// Which type's version history is open (anchored on the latest CV's id for that type).
	const [historyCv, setHistoryCv] = useState<{ id: string; label: string } | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [selectedTypes, setSelectedTypes] = useState<Record<'standard' | 'blind' | 'consultant_notes', boolean>>({
		standard: true,
		blind: false,
		consultant_notes: true,
	});

	// Fetch generated CVs for this candidate
	const { data: cvsData, isLoading } = useQuery({
		queryKey: ['generated-cvs', sourcedCandidateId],
		queryFn: () => cvGenerationApi.listByCandidate(sourcedCandidateId)
	});

	// Generate CVs mutation
	const generateMutation = useMutation({
		mutationFn: (types: ('standard' | 'blind' | 'consultant_notes')[]) => cvGenerationApi.generate({
			sourcedCandidateId,
			hiringRequestId,
			types
		}),
		onSuccess: () => {
			toast({ title: 'CV Generation Started', description: 'Generating the CVs. This may take a moment.' });
			// Poll for results
			const interval = setInterval(() => {
				queryClient.invalidateQueries({ queryKey: ['generated-cvs', sourcedCandidateId] });
			}, 5000);
			setTimeout(() => clearInterval(interval), 60000);
		},
		onError: (err: Error) => {
			toast({ title: 'Error', description: err.message, variant: 'destructive' });
		}
	});

	// Regenerate a specific CV
	const regenerateMutation = useMutation({
		mutationFn: ({ id, type }: { id: string; type: 'standard' | 'blind' | 'consultant_notes' }) =>
			cvGenerationApi.regenerate(id, type),
		onSuccess: () => {
			toast({ title: 'Regeneration Started', description: 'Regenerating this CV.' });
			const interval = setInterval(() => {
				queryClient.invalidateQueries({ queryKey: ['generated-cvs', sourcedCandidateId] });
			}, 5000);
			setTimeout(() => clearInterval(interval), 60000);
		}
	});

	// Version history for the currently-open type.
	const { data: historyData, isLoading: isHistoryLoading } = useQuery({
		queryKey: ['cv-history', historyCv?.id],
		queryFn: () => cvGenerationApi.getHistory(historyCv!.id),
		enabled: !!historyCv
	});
	const historyVersions = historyData?.data || [];

	// Restore an older version (copies it forward as the new latest version).
	const restoreMutation = useMutation({
		mutationFn: (id: string) => cvGenerationApi.restore(id),
		onSuccess: (res) => {
			const from = res.data?.restoredFromVersion;
			toast({ title: 'Restored', description: from ? `Version v${from} restored as the latest.` : 'Version restored.' });
			setHistoryCv(null);
			queryClient.invalidateQueries({ queryKey: ['generated-cvs', sourcedCandidateId] });
		},
		onError: (err: Error) => toast({ title: 'Restore Failed', description: err.message, variant: 'destructive' })
	});

	const cvs = cvsData?.data || [];
	const hasCVs = cvs.length > 0;
	// Number of versions per type — drives whether the History action is offered.
	const versionCountByType = cvs.reduce((acc: Record<string, number>, cv: any) => {
		acc[cv.type] = (acc[cv.type] || 0) + 1;
		return acc;
	}, {} as Record<string, number>);
	const hasBlindCV = cvs.some((c: any) => c.type === 'blind');
	const [includeBlindChoice, setIncludeBlindChoice] = useState<boolean | null>(null);
	const includeBlindOnRegenerate = includeBlindChoice ?? hasBlindCV;

	// Upload CV mutation
	const uploadMutation = useMutation({
		mutationFn: (file: File) => sourcingApi.uploadCandidateCV(sourcedCandidateId, file),
		onSuccess: (res) => {
			toast({ title: 'CV Uploaded', description: `Uploaded ${res.data.cvOriginalName}` });
			onCvUploaded?.();
		},
		onError: (err: Error) => {
			toast({ title: 'Upload Failed', description: err.message, variant: 'destructive' });
		}
	});

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		if (file.size > 10 * 1024 * 1024) {
			toast({ title: 'File Too Large', description: 'Maximum file size is 10MB', variant: 'destructive' });
			return;
		}
		uploadMutation.mutate(file);
		e.target.value = '';
	};

	// Group by type, take latest version
	const cvByType = cvs.reduce((acc: Record<string, any>, cv: any) => {
		if (!acc[cv.type] || cv.version > acc[cv.type].version) {
			acc[cv.type] = cv;
		}
		return acc;
	}, {} as Record<string, any>);

	return (
		<div className="space-y-4 pt-2">
			{/* Hidden file input */}
			<input
				ref={fileInputRef}
				type="file"
				accept=".pdf"
				className="hidden"
				onChange={handleFileSelect}
			/>

			{/* Data source indicators */}
			<div className="flex items-center gap-2 flex-wrap">
				{hasCvFile ? (
					<Badge variant="outline" className="text-xs gap-1 border-green-300 text-green-700 bg-green-50">
						<CheckCircle className="h-3 w-3" />
						CV Uploaded{cvOriginalName ? `: ${cvOriginalName}` : ''}
					</Badge>
				) : (
					<Badge variant="outline" className="text-xs gap-1 border-amber-300 text-amber-700 bg-amber-50">
						<AlertCircle className="h-3 w-3" />
						No CV Uploaded
					</Badge>
				)}
				{hasSignalHireData && (
					<Badge variant="outline" className="text-xs gap-1 border-blue-300 text-blue-700 bg-blue-50">
						<CheckCircle className="h-3 w-3" />
						Profile Data Available
					</Badge>
				)}
				<Button
					variant="outline"
					size="sm"
					className="h-6 text-xs"
					onClick={() => fileInputRef.current?.click()}
					disabled={uploadMutation.isPending}
				>
					<Upload className="h-3 w-3 mr-1" />
					{uploadMutation.isPending ? 'Uploading...' : hasCvFile ? 'Replace CV' : 'Upload CV'}
				</Button>
				{hasCvFile && (
					<Button
						variant="outline"
						size="sm"
						className="h-6 text-xs"
						onClick={async () => {
							try {
								const { blob, filename } = await sourcingApi.downloadOriginalCV(sourcedCandidateId);
								const url = URL.createObjectURL(blob);
								const a = document.createElement('a');
								a.href = url;
								a.download = filename || cvOriginalName || 'original-cv.pdf';
								a.click();
								URL.revokeObjectURL(url);
							} catch {
								toast({ title: 'Error', description: 'Failed to download original CV', variant: 'destructive' });
							}
						}}
					>
						<Download className="h-3 w-3 mr-1" /> Original CV
					</Button>
				)}
			</div>
			{/* Generated CVs */}
			{isLoading ? (
				<div className="flex items-center justify-center py-4">
					<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
				</div>
			) : hasCVs ? (
				<div className="space-y-3">
					<h4 className="text-sm font-semibold">Generated CVs</h4>
					{CV_TYPES.map(({ value, label, description }) => {
						const cv = cvByType[value];
						const isBlind = value === 'blind';
						// Type not generated yet (e.g. Blind CV was unchecked at first creation).
						// Always offer a way to generate it so the option never disappears.
						if (!cv) {
							return (
								<div key={value} className="flex items-center justify-between p-3 border border-dashed rounded-md">
									<div className="flex items-center gap-2">
										<FileText className="h-4 w-4 text-muted-foreground" />
										<div>
											<div className="text-sm font-medium">
												{label}
												<span className="text-xs text-muted-foreground ml-2">Not generated</span>
											</div>
											<div className="text-xs text-muted-foreground">{description}</div>
										</div>
									</div>
									<Button
										variant="outline"
										size="sm"
										onClick={() => generateMutation.mutate([value])}
										disabled={generateMutation.isPending || (!hasCvFile && !hasSignalHireData)}
									>
										<FileText className="h-3 w-3 mr-1" /> Generate
									</Button>
								</div>
							);
						}
						return (
							<div key={value} className={`flex items-center justify-between p-3 border rounded-md ${isBlind && includeBlindOnRegenerate ? 'bg-muted/30 border-primary/40' : ''}`}>
								<div className="flex items-center gap-2">
									{isBlind ? (
										<input
											type="checkbox"
											className="h-4 w-4 accent-primary shrink-0"
											checked={includeBlindOnRegenerate}
											onChange={(e) => setIncludeBlindChoice(e.target.checked)}
											title="Include in Regenerate All CVs"
										/>
									) : (
										<FileText className="h-4 w-4 text-muted-foreground" />
									)}
									<div>
										<span className="text-sm font-medium">{label}</span>
										<span className="text-xs text-muted-foreground ml-2">v{cv.version}</span>
										{cv.blindAlias && (
											<span className="text-xs text-muted-foreground ml-2">({cv.blindAlias})</span>
										)}
									</div>
								</div>
								<div className="flex items-center gap-1">
									<Button
										variant="ghost"
										size="sm"
										onClick={() => setPreviewCV({ content: cv.content, type: label, id: cv.id })}
									>
										<Eye className="h-3 w-3 mr-1" /> Preview
									</Button>
									<Button
										variant="ghost"
										size="sm"
										onClick={async () => {
											try {
											const { blob, filename } = await cvGenerationApi.downloadPDF(cv.id);
											const url = URL.createObjectURL(blob);
											const a = document.createElement('a');
											a.href = url;
											a.download = filename || `cv-${value}-v${cv.version}.pdf`;
												a.click();
												URL.revokeObjectURL(url);
											} catch {
												toast({ title: 'Error', description: 'PDF download failed', variant: 'destructive' });
											}
										}}
									>
										<Download className="h-3 w-3 mr-1" /> PDF
									</Button>
									{(versionCountByType[value] || 0) >= 2 && (
										<Button
											variant="ghost"
											size="sm"
											title={`Version history (${versionCountByType[value]} versions)`}
											onClick={() => setHistoryCv({ id: cv.id, label })}
										>
											<History className="h-3 w-3" />
										</Button>
									)}
									<Button
										variant="ghost"
										size="sm"
										onClick={() => regenerateMutation.mutate({ id: cv.id, type: value })}
										disabled={regenerateMutation.isPending}
									>
										<RefreshCw className="h-3 w-3" />
									</Button>
								</div>
							</div>
						);
					})}
				</div>
			) : null}

			{/* Generate new CVs */}
			{!hasCVs && (
				<div className="space-y-3">
					<h4 className="text-sm font-semibold">Generate CVs</h4>
					{!hasCvFile && !hasSignalHireData ? (
						<div className="p-4 border border-dashed rounded-md text-center">
							<FileText className="h-8 w-8 text-muted-foreground/50 mx-auto mb-2" />
							<p className="text-sm text-muted-foreground">
								Candidate must have an uploaded CV or SignalHire data before generation can begin.
							</p>
						</div>
					) : (
						<>
							<p className="text-sm text-muted-foreground">
							{hasCvFile && hasSignalHireData
								? 'The system will combine the uploaded CV with enriched profile data to generate professional formats:'
								: hasCvFile
									? 'The system will transform the candidate\'s uploaded CV into professional formats:'
									: 'The system will use enriched profile data to generate professional CV formats:'}
							</p>
							<div className="space-y-2">
								{CV_TYPES.map(({ value, label, description, required }) => {
									const checked = selectedTypes[value];
									return (
										<label
											key={value}
											className={`flex items-center gap-3 p-3 border rounded-md transition-colors ${required ? 'cursor-not-allowed opacity-90' : 'cursor-pointer'} ${checked ? 'bg-muted/30 border-primary/40' : 'bg-background hover:bg-muted/20'}`}
										>
											<input
												type="checkbox"
												className="h-4 w-4 accent-primary shrink-0"
												checked={checked}
												disabled={required}
												onChange={(e) => {
													if (required) return;
													setSelectedTypes(prev => ({ ...prev, [value]: e.target.checked }));
												}}
											/>
											<div>
												<div className="text-sm font-medium flex items-center gap-2">
													{label}
													{required && <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Required</span>}
												</div>
												<div className="text-xs text-muted-foreground">{description}</div>
											</div>
										</label>
									);
								})}
							</div>
							<Button
								onClick={() => {
									const types = (Object.keys(selectedTypes) as Array<'standard' | 'blind' | 'consultant_notes'>)
										.filter(t => selectedTypes[t]);
									if (types.length === 0) {
										toast({ title: 'Select at least one CV type', variant: 'destructive' });
										return;
									}
									generateMutation.mutate(types);
								}}
								disabled={generateMutation.isPending}
								className="w-full"
							>
								{generateMutation.isPending ? 'Starting Generation...' : 'Generate CVs'}
							</Button>
						</>
					)}
				</div>
			)}

			{/* Regenerate all button if CVs exist */}
			{hasCVs && (
				<Button
					variant="outline"
					size="sm"
					onClick={() => {
						const existing = Array.from(new Set(cvs.map((c: any) => c.type))) as ('standard' | 'blind' | 'consultant_notes')[];
						const baseTypes = existing.filter(t => t !== 'blind');
						const types: ('standard' | 'blind' | 'consultant_notes')[] = includeBlindOnRegenerate
							? Array.from(new Set([...baseTypes, 'blind']))
							: baseTypes;
						if (types.length === 0) return;
						generateMutation.mutate(types);
					}}
					disabled={generateMutation.isPending}
					className="w-full"
				>
					<RefreshCw className="h-3 w-3 mr-1" /> Regenerate All CVs
				</Button>
			)}

			{/* Preview modal */}
			{previewCV && (
				<CVPreviewModal
					open={!!previewCV}
					onOpenChange={() => setPreviewCV(null)}
					content={previewCV.content}
					title={previewCV.type}
					cvId={previewCV.id}
					onContentSaved={(newContent) => {
						setPreviewCV(prev => prev ? { ...prev, content: newContent } : null);
						queryClient.invalidateQueries({ queryKey: ['generated-cvs', sourcedCandidateId] });
					}}
				/>
			)}

			{/* Version history dialog */}
			{historyCv && (
				<Dialog open={!!historyCv} onOpenChange={(open) => { if (!open) setHistoryCv(null); }}>
					<DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
						<DialogHeader>
							<DialogTitle>Version History: {historyCv.label}</DialogTitle>
						</DialogHeader>
						<div className="space-y-3 py-2">
							{isHistoryLoading ? (
								<div className="text-center py-4 text-sm text-muted-foreground">Loading history...</div>
							) : historyVersions.length === 0 ? (
								<div className="text-center py-4 text-sm text-muted-foreground">No history available</div>
							) : (
								historyVersions.map((item: any, idx: number) => (
									<div key={item.id} className="border rounded-lg p-3 flex flex-col gap-2">
										<div className="flex justify-between items-start gap-2">
											<div>
												<div className="flex items-center gap-2 flex-wrap">
													<Badge variant="outline">v{item.version}</Badge>
													{idx === 0 && <Badge variant="secondary" className="text-[10px]">Current</Badge>}
													<span className="text-xs text-muted-foreground">
														{item.createdAt ? new Date(item.createdAt).toLocaleString() : 'Unknown date'}
													</span>
												</div>
												<div className="text-xs text-muted-foreground mt-1">
													By: {item.creator?.name || 'System'}
													{item.aiModel ? ` • ${item.aiModel}` : ''}
													{item.blindAlias ? ` • ${item.blindAlias}` : ''}
												</div>
											</div>
											<div className="flex items-center gap-1 shrink-0">
												<Button
													size="sm"
													variant="ghost"
													onClick={() => { setPreviewCV({ content: item.content, type: `${historyCv.label} v${item.version}`, id: item.id }); setHistoryCv(null); }}
												>
													<Eye className="h-3 w-3 mr-1" /> Preview
												</Button>
												{idx !== 0 && (
													<Button
														size="sm"
														variant="secondary"
														onClick={() => restoreMutation.mutate(item.id)}
														disabled={restoreMutation.isPending}
													>
														<RotateCcw className="h-3 w-3 mr-1" /> Restore
													</Button>
												)}
											</div>
										</div>
										<div className="text-xs text-muted-foreground line-clamp-2 bg-muted p-2 rounded">
											{(item.content || '').replace(/[#*`>]/g, '').slice(0, 160)}…
										</div>
									</div>
								))
							)}
						</div>
					</DialogContent>
				</Dialog>
			)}
		</div>
	);
}
